import { useState } from "react";
import type { CounterpartyTinReview } from "@common/schemas/counterpartyTin.schema";
import type { TinType } from "@common/types/counterparty";
import {
    type CounterpartyTinIds,
    useClearTinMutation,
} from "@api/counterpartyTin.api";
import { HideIcon, ShowIcon } from "@icons";
import { Button } from "@design-system/Button/Button";
import { HStack } from "@design-system/HStack/HStack";
import { InputRoot, InputSlot } from "@design-system/Input/InputParts";
import { Menu } from "@design-system/Menu/Menu.lib";
import { ConfirmationModal } from "@design-system/Modal/ConfirmationModal";
import { useToaster } from "@design-system/ToastMessages/useToaster";
import { VStack } from "@design-system/VStack/VStack";
import type { VgsConfig } from "@lib/vgs/vgsConfig";
import { signInAgain } from "./signInAgain";
import { TIN_MASKS, TIN_TYPE_LABELS } from "./tinType";
import { TinReviewStatus } from "./TinReviewStatus";
import { TinTypePicker } from "./TinTypePicker";
import { useVgsShowReveal } from "./useVgsShowReveal";
import styles from "./CounterpartyTinSection.module.scss";

interface TinSavedStateProps {
    ids: CounterpartyTinIds;
    config: VgsConfig;
    tinType: TinType;
    tinRevisionId: string | null;
    reviews: CounterpartyTinReview[];
    onUpdate: () => void;
}

/** Remounts per revision so a value revealed before the tax ID changed never sits next to the new revision's Mark reviewed. */
export const TinSavedState = (props: TinSavedStateProps) => (
    <TinSavedStateForRevision key={props.tinRevisionId} {...props} />
);

const TinSavedStateForRevision = ({
    ids,
    config,
    tinType,
    tinRevisionId,
    reviews,
    onUpdate,
}: TinSavedStateProps) => {
    const toaster = useToaster();
    const { containerRef, status, reveal, hide } = useVgsShowReveal({
        ids,
        config,
    });
    const clearTinMutation = useClearTinMutation(ids);
    const [isConfirmingDelete, setIsConfirmingDelete] = useState(false);
    const isRevealed = status === "revealed";
    const tinLabel = TIN_TYPE_LABELS[tinType];

    const closeConfirmation = () => setIsConfirmingDelete(false);

    const deleteTin = async () => {
        try {
            await clearTinMutation.mutateAsync();
        } catch {
            return;
        }
        closeConfirmation();
        toaster.success("Deleted tax ID");
    };

    return (
        <VStack gap={8}>
            <HStack gap={8} align="center">
                <TinTypePicker value={tinType} disabled />
                <InputRoot
                    size="lg"
                    className={styles.tinField}
                    role="group"
                    aria-label={`${tinLabel} on file`}
                >
                    <span
                        className={styles.maskedValue}
                        hidden={isRevealed}
                        aria-hidden
                    >
                        {TIN_MASKS[tinType]}
                    </span>
                    <div
                        ref={containerRef}
                        className={styles.revealFrame}
                        hidden={!isRevealed}
                        data-testid="tin-reveal-frame"
                    />
                    <InputSlot side="right">
                        <Button
                            type="button"
                            variant="secondary"
                            iconOnly
                            aria-label={`${isRevealed ? "Hide" : "Reveal"} ${tinLabel}`}
                            data-slot-box
                            disabled={status === "revealing"}
                            onClick={isRevealed ? hide : () => void reveal()}
                        >
                            {isRevealed ? <HideIcon /> : <ShowIcon />}
                        </Button>
                    </InputSlot>
                </InputRoot>
                <Menu.More aria-label="Tax ID actions" size="lg">
                    <Menu.Item onClick={onUpdate}>Update</Menu.Item>
                    <Menu.Item
                        destructive
                        onClick={() => setIsConfirmingDelete(true)}
                    >
                        Delete
                    </Menu.Item>
                </Menu.More>
            </HStack>
            {tinRevisionId && (
                <TinReviewStatus
                    ids={ids}
                    tinRevisionId={tinRevisionId}
                    reviews={reviews}
                />
            )}
            {status === "stepUpRequired" && (
                <HStack gap={8} align="center" wrap>
                    <span className={styles.message}>
                        Sign in again to reveal this tax ID
                    </span>
                    <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={signInAgain}
                    >
                        Sign in again
                    </Button>
                </HStack>
            )}
            <ConfirmationModal
                show={isConfirmingDelete}
                title="Delete tax ID?"
                question="The tax ID is permanently deleted from this counterparty."
                yes="Delete"
                no="Cancel"
                yesBtnVariant="destructive"
                onConfirm={deleteTin}
                onReject={closeConfirmation}
                onHide={closeConfirmation}
            />
        </VStack>
    );
};
